// components/WoPDF.jsx
import React, {useEffect, useState} from "react";
import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    PDFViewer,
    Image,
    PDFDownloadLink,
} from "@react-pdf/renderer";
import ConfirmationDlg from "../../../components/ConfirmationDlg";
import { formatDateOnly, renderImage } from "../../../utils/helper.jsx";

const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontSize: 12,
        fontFamily: "Helvetica",
    },
    section: {
        marginBottom: 10,
    },
    row: {
        border: "1px solid grey",
        flexDirection: "row",
        alignItems: "flex-start",
        width: "100%",
    },
    widthLabel: {
        backgroundColor: "#1e2835",
        color: "white",
        padding: "5px",
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        flexShrink: 0,
    },
    /** Wraps value text so long strings (e.g. location) wrap inside the page. */
    valueCell: {
        flex: 1,
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
        padding: "5px",
    },
    labelValue: {
        fontSize: 10,
        lineHeight: 1.4,
    },
    title: {
        fontSize: 18,
        marginBottom: 10,
    },
    header: {
        fontSize: 20,
        fontWeight:"bold",
        marginBottom: 10,
        borderBottom:"2px solid black"
    },
    marginBottom:{
        marginBottom:10
    },
    assetContainer: {
        borderBottomWidth: 1,
        paddingBottom: 5,
        flexDirection: "row",
        alignItems: "flex-start",
        width: "100%",
    },
    assetText: {
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
        fontSize: 10,
        lineHeight: 1.35,
        paddingRight: 8,
    },
    image: {
        marginRight: 10,
        width: 80,
        height: 80,
        objectFit: "cover",
    },
});

/** Walk nested location (leaf → root), then display root → leaf. */
function getPdfLocationDisplay(data) {
    if (!data) return "—";
    const loc = data.asset?.location || data.location;
    if (loc && typeof loc === "object" && loc.location_name) {
        const names = [];
        function walk(node) {
            if (!node?.location_name) return;
            names.push(node.location_name);
            if (node.parent_location) walk(node.parent_location);
        }
        walk(loc);
        return names.length ? names.reverse().join(" > ") : "—";
    }
    if (typeof data.location === "string" && data.location.trim()) return data.location;
    return "—";
}

/** Walk nested problem_type (child → parent), then display parent → child. */
function getPdfProblemTypeDisplay(data) {
    if (!data) return "—";
    const pt = data.problem_type;
    if (pt && typeof pt === "object" && pt.problem_type_name) {
        const names = [];
        function walk(node) {
            if (!node?.problem_type_name) return;
            names.push(node.problem_type_name);
            if (node.parent_problem_type) walk(node.parent_problem_type);
        }
        walk(pt);
        return names.length ? names.reverse().join(" - ") : "—";
    }
    if (typeof pt === "string" && pt.trim()) return pt;
    return "—";
}

function getPdfAssetDisplay(data) {
    if (!data?.asset) return "—";
    if (typeof data.asset === "string") return data.asset;
    if (typeof data.asset === "object" && data.asset.asset_name) return data.asset.asset_name;
    return "—";
}

function PdfFieldRow({ label, children }) {
    return (
        <View style={styles.row}>
            <Text style={styles.widthLabel}>{label}</Text>
            <View style={styles.valueCell}>
                <Text style={styles.labelValue}>{children}</Text>
            </View>
        </View>
    );
}

const DocumentPreview = ({ data, attachments }) => (
  <Document title={data.title} subject={data.title}>
    <Page style={styles.page}>
      <View style={styles.section}>
        <Text style={styles.header}>Work Order</Text>
        <PdfFieldRow label="Title:">{data.title}</PdfFieldRow>
        <PdfFieldRow label="Location:">{getPdfLocationDisplay(data)}</PdfFieldRow>
        <PdfFieldRow label="WO number:">
          {data.number != null && data.number !== "" ? `WO#${data.number}` : "—"}
        </PdfFieldRow>
        <PdfFieldRow label="Problem type:">{getPdfProblemTypeDisplay(data)}</PdfFieldRow>
        <PdfFieldRow label="Created:">{formatDateOnly(data.created_at)}</PdfFieldRow>
        <PdfFieldRow label="Description:">{data.description}</PdfFieldRow>
        <PdfFieldRow label="Status:">{data.status}</PdfFieldRow>
        <PdfFieldRow label="Asset:">{getPdfAssetDisplay(data)}</PdfFieldRow>
        <PdfFieldRow label="Priority:">{data.priority}</PdfFieldRow>
        <PdfFieldRow label="Assigned to:">{data.assigned}</PdfFieldRow>
      </View>

      {attachments && (
        <View style={styles.section}>
          {attachments.length && <Text style={styles.title}>Attachment</Text>}
          {attachments.map((attachment, index) =>
            attachment.type === "image" ? (
              <View key={index} style={styles.assetContainer}>
                <Image style={styles.image} src={attachment.url} />
              </View>
            ) : null,
          )}
        </View>
      )}

      <View style={styles.section}>
        <Text style={styles.title}>Tasks</Text>
        {data?.tasks
          ?.filter((z) => !z.isRequest)
          .map((task, index) => (
            <View style={styles.marginBottom}>
              <View key={index} style={styles.assetContainer}>
                <Text style={styles.assetText}>{task.task_name}</Text>
                <Text>{task.task_option_selected || task.task_status}</Text>
              </View>
              {task.task_photo && (
                <View style={styles.row}>
                  <Image style={styles.image} src={task.url_full} />
                  <Text>Note : {data.task_note}</Text>
                </View>
              )}
            </View>
          ))}
      </View>
      <View style={styles.section}>
        <Text style={styles.title}>Parts</Text>
        {data?.inventories?.map((task, index) => (
          <View>
            <View key={index} style={styles.assetContainer}>
              <Text style={styles.assetText}>
                {task.inventory?.part?.part_name}
              </Text>
              <Text>{task.quantity} pcs</Text>
            </View>
            {task.task_photo && (
              <View style={styles.row}>
                <Image style={styles.image} src={task.url_full} />
                <Text>Note : {data.task_note}</Text>
              </View>
            )}
          </View>
        ))}
      </View>
    </Page>
    {data.request_status ? (
      <Page style={styles.page}>
        <View style={styles.section}>
          <Text style={styles.header}>Request</Text>
          <PdfFieldRow label="Title:">{data.title}</PdfFieldRow>
          <PdfFieldRow label="Location:">{getPdfLocationDisplay(data)}</PdfFieldRow>
          <PdfFieldRow label="WO number:">
            {data.number != null && data.number !== "" ? `WO#${data.number}` : "—"}
          </PdfFieldRow>
          <PdfFieldRow label="Problem type:">{getPdfProblemTypeDisplay(data)}</PdfFieldRow>
          <PdfFieldRow label="Created:">{formatDateOnly(data.created_at)}</PdfFieldRow>
          <PdfFieldRow label="Description:">{data.description}</PdfFieldRow>
          <PdfFieldRow label="Status:">{data.status}</PdfFieldRow>
          <PdfFieldRow label="Asset:">{getPdfAssetDisplay(data)}</PdfFieldRow>
          <PdfFieldRow label="Priority:">{data.priority}</PdfFieldRow>
          <PdfFieldRow label="Assigned to:">{data.assigned}</PdfFieldRow>
        </View>

        {attachments && (
          <View style={styles.section}>
            {attachments.length && <Text style={styles.title}>Attachment</Text>}
            {attachments.map((attachment, index) =>
              attachment.type === "image" ? (
                <View key={index} style={styles.assetContainer}>
                  <Image style={styles.image} src={attachment.url} />
                </View>
              ) : null,
            )}
          </View>
        )}

        <View style={styles.section}>
          <Text style={styles.title}>Tasks</Text>
          {data?.tasks
            ?.filter((z) => z.is_request)
            .map((task, index) => (
              <View style={styles.marginBottom}>
                <View key={index} style={styles.assetContainer}>
                  <Text style={styles.assetText}>{task.task_name}</Text>
                  <Text>{task.task_option_selected || task.task_status}</Text>
                </View>
                {task.task_photo && (
                  <View style={styles.row}>
                    <Image style={styles.image} src={task.url_full} />
                    <Text>Note : {data.task_note}</Text>
                  </View>
                )}
              </View>
            ))}
        </View>
      </Page>
    ) : (
      <></>
    )}
  </Document>
);

const DocumentPreviewTest = ({ data, attachments }) => (
    <Document title={"data.title"} subject={"data.title"}>
        <Page style={styles.page}>
            <Text>Note </Text>
        </Page>

    </Document>
);

const WoPDF = ({ data, open, setOpen, attachments }) => {
    const [view,setView] = useState(false)
    const handleDownload = () => {
        setOpen(false);
    };
    useEffect(() => {
        if(open){
            setTimeout(()=>{
                setView(true)
            },[500])
        }else{
            setView(false)
        }
    }, [open]);
    return (
        <ConfirmationDlg
            open={open}
            size={"lg"}
            hideCancelBtn={true}
            btnSubmitText={"Close"}
            onClose={() => setOpen(false)}
            onSubmit={handleDownload}
        >
            {view ? <>
                {/*<div style={{ width: "100%", height: "500px", marginBottom: "20px" }}>*/}
                {/*    <PDFViewer width="100%" height="100%">*/}
                {/*        <DocumentPreview data={data} attachments={attachments}  />*/}
                {/*    </PDFViewer>*/}
                {/*</div>*/}
                <div>
                    You will download the report in PDF Format
                </div>

                <PDFDownloadLink
                    document={<DocumentPreview data={data}  attachments={attachments}  />}
                    fileName={`${data.title}.pdf`}
                    className={"btn btn-theme text-black"}
                >
                    {({ loading }) =>
                        loading ? "Generating PDF..." : <> <i className="fa fa-download"></i> Download PDF</>
                    }
                </PDFDownloadLink>
            </>  : null}

        </ConfirmationDlg>
    );
};

export default WoPDF;
